import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ScreenshotTimelineEntry } from '../../types/ScreenshotTypes';
import { screenshotCaption } from './screenshotUtils';

interface ScreenshotModalProps {
  open: boolean;
  onClose: () => void;
  /** Newest first, same order as the strip. */
  entries: ScreenshotTimelineEntry[];
  index: number;
  onIndexChange: (index: number) => void;
  domain?: string;
}

/**
 * Full-size viewer for one capture with prev/next (and keyboard arrows) over
 * the domain's history. Left = newer, right = older, matching the strip's
 * newest-first order (WHEEL-394).
 */
const ScreenshotModal = ({
  open,
  onClose,
  entries,
  index,
  onIndexChange,
  domain,
}: ScreenshotModalProps) => {
  const entry = entries[index];
  const hasNewer = index > 0;
  const hasOlder = index < entries.length - 1;

  const goNewer = () => hasNewer && onIndexChange(index - 1);
  const goOlder = () => hasOlder && onIndexChange(index + 1);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goNewer();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goOlder();
    }
  };

  const finalUrl = entry?.final_url ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      onKeyDown={handleKeyDown}
      data-testid="screenshot-modal"
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography component="span" variant="h6" sx={{ display: 'block' }}>
          {domain || 'Screenshot'}
        </Typography>
        {entry && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="screenshot-modal-caption"
          >
            {screenshotCaption(entry)}
            {entries.length > 1 && ` · ${index + 1} / ${entries.length}`}
          </Typography>
        )}
        {finalUrl && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', wordBreak: 'break-all' }}
          >
            {finalUrl}
          </Typography>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        data-testid="screenshot-modal-close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.secondary',
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 0, overflow: 'hidden' }}>
        <Box sx={{ position: 'relative' }}>
          {entry && (
            <Box
              component="img"
              src={entry.url}
              alt={screenshotCaption(entry)}
              data-testid="screenshot-modal-image"
              sx={{
                // Fit inside the viewport: the dialog title takes ~110px, so
                // a 1366x768 capture never forces the content area to scroll.
                display: 'block',
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 190px)',
                width: 'auto',
                height: 'auto',
                mx: 'auto',
                borderRadius: '8px',
                border: 1,
                borderColor: 'divider',
              }}
            />
          )}
          {entries.length > 1 && (
            <>
              <Tooltip title="Newer capture">
                <span
                  style={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    visibility: hasNewer ? 'visible' : 'hidden',
                  }}
                >
                  <IconButton
                    onClick={goNewer}
                    disabled={!hasNewer}
                    data-testid="screenshot-modal-prev"
                    sx={{
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Older capture">
                <span
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    visibility: hasOlder ? 'visible' : 'hidden',
                  }}
                >
                  <IconButton
                    onClick={goOlder}
                    disabled={!hasOlder}
                    data-testid="screenshot-modal-next"
                    sx={{
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ScreenshotModal;
